import { clearAuthToken, getAuthToken } from '@/lib/auth/auth-token';
import { persistAuthTokenFromResponse } from '@/lib/auth/persist-auth-token';
import { normalizeEmail } from '@/lib/helpers/normalize-email';
import { axiosInstance } from '@/lib/utils';
import { endpoints } from '@/api';
import type {
  AuthResponse,
  GoogleCompleteSignupResponse,
  OTPVerificationResponse,
  ResetPasswordResponse,
  TwoFactorSetupResponse,
  TwoFactorVerifyResponse,
  InitializeDisable2FAResponse,
  TwoFactorDisableResponse,
} from './response';
import type {
  GoogleCompleteSignupPayload,
  GoogleLoginPayload,
  LoginPayload,
  NewPasswordPayload,
  OAuthPayload,
  OTPRequestPayload,
  OTPVerificationPayload,
  ChangePasswordPayload,
  TwoFactorSetupPayload,
  TwoFactorVerifyPayload,
  TwoFactorDisablePayload,
} from './payload';
import type { RequestResponse } from '@/types';

export const login = async (payload: LoginPayload) => {
  const response = await axiosInstance.post<AuthResponse>(endpoints.login, {
    ...payload,
    email: normalizeEmail(payload.email),
  });
  persistAuthTokenFromResponse(response.data);
  return response.data;
};

export const requestOTP = async (payload: OTPRequestPayload) => {
  const response = await axiosInstance.post<RequestResponse>(
    endpoints.requestOTP,
    {
      ...payload,
      identifier:
        payload.channel === 'email'
          ? normalizeEmail(payload.identifier)
          : payload.identifier,
    }
  );
  return response.data;
};

export const verifyOTP = async (payload: OTPVerificationPayload) => {
  const response = await axiosInstance.post<OTPVerificationResponse>(
    endpoints.verifyOTP,
    payload
  );
  persistAuthTokenFromResponse(response.data);
  return response.data;
};

export const createNewPassword = async (payload: NewPasswordPayload) => {
  const response = await axiosInstance.post<ResetPasswordResponse>(
    endpoints.resetPassword,
    payload
  );
  return response.data;
};

export const createOAuth = async (payload: OAuthPayload) => {
  const response = await axiosInstance.post<AuthResponse>(
    endpoints.createOAuth,
    payload
  );
  persistAuthTokenFromResponse(response.data);

  if (!getAuthToken()) {
    const loginResponse = await loginOAuth(payload);
    persistAuthTokenFromResponse(loginResponse);
  }

  return response.data;
};

export const loginOAuth = async (payload: OAuthPayload) => {
  const response = await axiosInstance.post<AuthResponse>(
    endpoints.loginOAuth,
    payload
  );
  persistAuthTokenFromResponse(response.data);
  return response.data;
};

export const loginGoogle = async (payload: GoogleLoginPayload) => {
  const response = await axiosInstance.post<AuthResponse>(
    endpoints.loginGoogle,
    payload
  );
  // Persists session `token` (Response B). Does not treat `setupToken` as session auth.
  persistAuthTokenFromResponse(response.data);
  return response.data;
};

/** Public — completes Google profile using setupToken from login/google Response A. */
export const completeGoogleSignup = async (
  payload: GoogleCompleteSignupPayload
) => {
  const { referrerCode, ...rest } = payload;
  const response = await axiosInstance.post<GoogleCompleteSignupResponse>(
    endpoints.completeGoogleSignup,
    {
      ...rest,
      ...(referrerCode?.trim() ? { referrerCode: referrerCode.trim() } : {}),
    }
  );
  persistAuthTokenFromResponse(response.data);
  return response.data;
};

export const logout = async () => {
  try {
    const response = await axiosInstance.post<AuthResponse>(endpoints.logout);
    return response.data;
  } finally {
    clearAuthToken();
  }
};

export const changePassword = async (payload: ChangePasswordPayload) => {
  const response = await axiosInstance.post<RequestResponse>(
    endpoints.changePassword,
    payload
  );
  return response.data;
};

export const setupTwoFactor = async (payload: TwoFactorSetupPayload) => {
  const response = await axiosInstance.post<TwoFactorSetupResponse>(
    endpoints.twoFactor.setup,
    payload
  );
  return response.data;
};

export const verifyTwoFactor = async (payload: TwoFactorVerifyPayload) => {
  const response = await axiosInstance.post<TwoFactorVerifyResponse>(
    endpoints.twoFactor.verify,
    payload
  );
  return response.data;
};

export const initializeDisableTwoFactor = async () => {
  const response = await axiosInstance.post<InitializeDisable2FAResponse>(
    endpoints.twoFactor.initializeDisable
  );
  return response.data;
};

export const disableTwoFactor = async (payload: TwoFactorDisablePayload) => {
  const response = await axiosInstance.post<TwoFactorDisableResponse>(
    endpoints.twoFactor.disable,
    payload
  );
  return response.data;
};
